import { Bot, Code2, Rocket, Search } from "lucide-react";
import { QuoteBuilder } from "@/components/landing/QuoteBuilder";
import { PayPalCheckout } from "@/components/landing/PayPalCheckout";
import { SampleCertificate } from "@/pages/CertificatePage";
import { CourseFeatures } from "@/components/site/CourseFeatures";
import { ShareCourse } from "@/components/site/ShareArticle";
import { AI_COURSE_OUTCOME } from "@/lib/constants";
import { BookCallButton } from "@/components/site/BookCall";

const SECRETS = [
  "How I personally, single-handedly created and developed Google-ranked apps and websites",
  "The exact tools I use — and why",
  "The steps I took, from first idea to launch",
  "How to maintain the same quality on every project",
  "SEO architecture that helps you get found and make more money",
  "Turning websites into installable apps — and more!",
];
import { PageHero, OutLink } from "@/components/site/PageBits";
import { SectionLabel } from "@/components/site/FormBits";
import { AiCourseLeadForm } from "@/components/landing/AiCourseLeadForm";
import { useSeo, pageSeo } from "@/lib/seo";

const PORTFOLIO = "https://gregmiceli.com/portfolio";

const LEARN = [
  { icon: Bot, t: "Build with AI tools", d: "Use modern AI tools to plan, design, and build professional websites faster." },
  { icon: Code2, t: "Real web development", d: "Learn the structure behind a working site so you can edit, fix, and grow it — not just generate it." },
  { icon: Search, t: "SEO & marketing basics", d: "Make sites easier to find, with clear headings, pages, and content built around real search terms." },
  { icon: Rocket, t: "Launch for clients", d: "Turn a skill into an offer: scope a project, show proof, and launch websites for real businesses." },
];

const DETAILS = [
  ["Tuition", "Starting at $5,000"],
  ["Final price", "Depends on the extent of the teachings and add-ons"],
  ["Format & schedule", "[To be announced — host will update]"],
  ["Start dates", "[To be announced — host will update]"],
  ["Full curriculum", "[Host will add module list]"],
];

export default function AiCoursePage() {
  useSeo(pageSeo("/ai-website-developer-course"));
  return (
    <article>
      <PageHero eyebrow="COURSES · STARTING AT $5,000" title="AI Website Web Developer Course" image="/brand/ai-poster-a.jpg" imageClass="max-h-[620px] object-contain" imageAlt="Greg Miceli — 7-Day AI Website Developer Course: tools I use, idea to launch, Google-ranking SEO, websites into apps"
        subtitle="Learn to build professional websites with AI — from the developer who has designed, coded and launched over 75 websites in 2026 for companies and local businesses in Nicaragua, the United States, Canada and beyond.">
        <a href="#ai-course-info" className="inline-flex items-center gap-2 rounded-xl bg-[#F08A3C] text-white px-5 py-3 font-semibold hover:bg-[#D96F28]" data-testid="ai-course-info-cta">Request more information</a>
        <OutLink href={PORTFOLIO} testId="ai-course-portfolio-link" light>View my portfolio</OutLink>
        <ShareCourse path="/ai-website-developer-course" title="AI Website Web Developer Course with Greg Miceli" />
        <BookCallButton source="ai-course-hero" light testId="ai-course-book-call-btn" />
      </PageHero>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 pt-12" data-testid="ai-course-outcome">
        <div className="rounded-3xl border-2 border-[#F08A3C] bg-[#F08A3C]/5 p-7 sm:p-10">
          <p className="font-mono-ui text-xs tracking-[0.3em] text-[#D96F28]">WHAT YOU WALK AWAY WITH</p>
          <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A4A]">Your own working website or app, ready to generate leads</h2>
          <p className="mt-3 text-slate-700 leading-relaxed max-w-3xl">{AI_COURSE_OUTCOME} Lead forms, contact and booking flows, SEO setup and analytics are in place by the final day, so it can start bringing in enquiries the moment the course is complete. In-person sessions are hosted at Esc Digital Nomad Village in Popoyo, Nicaragua.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 pt-6" data-testid="ai-course-secrets">
        <div className="rounded-3xl bg-[#0B2A4A] text-[#F5ECD7] p-8 sm:p-12 grid lg:grid-cols-[1fr_1.2fr] gap-8">
          <div>
            <p className="font-mono-ui text-xs tracking-[0.3em] text-[#F08A3C]">INSIDE THE COURSE</p>
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold text-white">The SECRETS everyone wants to know</h2>
            <p className="mt-3 text-[#F5ECD7]/80">I'll teach you exactly how I do it — nothing held back.</p>
          </div>
          <div className="flex flex-col gap-6">
          <ul className="flex flex-col gap-3">
            {SECRETS.map((s) => <li key={s} className="flex gap-3"><span className="mt-2 h-2 w-2 rounded-full bg-[#F08A3C] shrink-0" /><span>{s}</span></li>)}
          </ul>
          <img src="/brand/ai-poster-b.jpg" alt="Build your business website yourself — plan it with AI, build it in HTML, turn it into a React app" className="w-full max-w-sm rounded-2xl" data-testid="ai-secrets-image" />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16">
        <SectionLabel>What you'll learn</SectionLabel>
        <h2 className="mt-3 mb-10 text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A4A]">Become an AI-powered web developer</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {LEARN.map(({ icon: Icon, t, d }) => (
            <div key={t} className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-6">
              <Icon className="h-6 w-6 text-[#1A9AA8]" />
              <h3 className="mt-4 font-semibold text-[#0B2A4A]">{t}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <CourseFeatures testId="ai-course-features" />

      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-16" data-testid="ai-course-addons">
        <SectionLabel>Add-ons</SectionLabel>
        <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A4A]">Build the course around your goals</h2>
        <p className="mt-3 mb-8 text-slate-600 max-w-2xl">The course starts at $5,000. Tap the add-ons you want to build your custom program — each is priced by scope with a custom quote.</p>
        <QuoteBuilder />
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-6">
        <div className="rounded-3xl bg-[#0B2A4A] text-[#F5ECD7] p-8 sm:p-12 flex flex-col md:flex-row md:items-center justify-between gap-6" data-testid="ai-course-portfolio-cta">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white">See the work before you enroll</h2>
            <p className="mt-2 text-[#F5ECD7]/80 max-w-xl">Browse websites and apps Greg has built for real businesses — the same kind of projects you'll learn to create.</p>
          </div>
          <OutLink href={PORTFOLIO} testId="ai-course-portfolio-cta-btn">View the portfolio at gregmiceli.com</OutLink>
        </div>
      </section>

      <SampleCertificate course="AI Website Development Course" how="Your certificate is awarded by Mr. Greg once you've completed the course work and launched your project — so it shows real skills you built." />

      <section id="ai-course-info" className="max-w-7xl mx-auto px-4 sm:px-8 py-16 grid lg:grid-cols-[1fr_1.3fr] gap-12 items-start scroll-mt-24">
        <div>
          <SectionLabel>Course details</SectionLabel>
          <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A4A]">Want more information?</h2>
          <p className="mt-3 text-slate-600">Fill out the form and Mr. Greg will reach out with details on the program.</p>
          <dl className="mt-8 flex flex-col gap-3 text-sm" data-testid="ai-course-details">
            {DETAILS.map(([k, v]) => (
              <div key={k} className="flex flex-col sm:flex-row sm:justify-between gap-1 border-b border-dashed border-[#0B2A4A]/15 pb-3">
                <dt className="font-semibold text-[#0B2A4A]">{k}</dt>
                <dd className={v.startsWith("[") ? "text-slate-500 italic" : k === "Tuition" ? "font-display text-lg font-bold text-[#F08A3C]" : "text-slate-700 sm:text-right"}>{v}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-6"><PayPalCheckout slot="details" /></div>
          <p className="mt-6 text-xs text-slate-500">No course can promise clients, income, or a job. You'll learn practical skills and build real projects.</p>
        </div>
        <div className="bg-white rounded-3xl shadow-xl shadow-[#0B2A4A]/10 border border-[#0B2A4A]/10 p-6 sm:p-10">
          <AiCourseLeadForm />
        </div>
      </section>
    </article>
  );
}
